import React, { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { CheckCircle2, AlertTriangle, ArrowLeft } from 'lucide-react';
import { apiClient } from '../../api/client';
import { parseApiError } from '../../api/errors';
import { Button, Input, AlertBanner } from '../../components/common';

export const ResetPasswordForm: React.FC = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  if (!token) {
    return (
      <div className="text-center py-4 space-y-4">
        <div className="w-12 h-12 rounded-full bg-amber-100 dark:bg-amber-950/60 flex items-center justify-center text-amber-600 dark:text-amber-400 mx-auto">
          <AlertTriangle className="w-6 h-6" />
        </div>
        <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100">
          Invalid Reset Link
        </h3>
        <p className="text-xs text-slate-600 dark:text-slate-400 leading-relaxed max-w-sm mx-auto">
          This password reset link is missing a valid token or has expired. Please request a new recovery link.
        </p>
        <div className="pt-2">
          <Link to="/forgot-password">
            <Button variant="primary" size="sm" className="w-full">
              Request New Link
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMessage(null);

    if (password.length < 8) {
      setErrorMessage('Password must be at least 8 characters long.');
      return;
    }

    if (password !== confirmPassword) {
      setErrorMessage('Passwords do not match.');
      return;
    }

    setIsLoading(true);
    try {
      const { error } = await apiClient.POST('/api/v1/auth/reset-password', {
        body: {
          token,
          new_password: password,
        },
      });

      if (error) {
        throw new Error(parseApiError(error).message);
      }

      setIsSuccess(true);
    } catch (err: any) {
      setErrorMessage(
        err?.message || 'Failed to reset password. The link may have expired or already been used.'
      );
    } finally {
      setIsLoading(false);
    }
  };

  if (isSuccess) {
    return (
      <div className="text-center py-4 space-y-4">
        <div className="w-12 h-12 rounded-full bg-emerald-100 dark:bg-emerald-950/60 flex items-center justify-center text-emerald-600 dark:text-emerald-400 mx-auto">
          <CheckCircle2 className="w-6 h-6" />
        </div>
        <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100">
          Password Reset Complete
        </h3>
        <p className="text-xs text-slate-600 dark:text-slate-400 leading-relaxed max-w-sm mx-auto">
          Your password has been successfully reset. You can now sign in to your PriceLoss account using your new credentials.
        </p>
        <div className="pt-2">
          <Link to="/login">
            <Button variant="primary" size="sm" className="w-full">
              Proceed to Sign In
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {errorMessage && (
        <AlertBanner
          variant="error"
          message={errorMessage}
          onClose={() => setErrorMessage(null)}
        />
      )}

      <div>
        <label
          htmlFor="new-password"
          className="block text-xs font-medium text-slate-700 dark:text-slate-300 mb-1"
        >
          New Password
        </label>
        <Input
          id="new-password"
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          placeholder="At least 8 characters"
          required
          disabled={isLoading}
        />
      </div>

      <div>
        <label
          htmlFor="confirm-password"
          className="block text-xs font-medium text-slate-700 dark:text-slate-300 mb-1"
        >
          Confirm New Password
        </label>
        <Input
          id="confirm-password"
          type="password"
          value={confirmPassword}
          onChange={(e) => setConfirmPassword(e.target.value)}
          placeholder="Re-enter your new password"
          required
          disabled={isLoading}
        />
      </div>

      <Button
        type="submit"
        variant="primary"
        size="md"
        className="w-full mt-2"
        disabled={isLoading}
      >
        {isLoading ? 'Resetting Password...' : 'Set New Password'}
      </Button>

      <div className="text-center pt-2">
        <Link
          to="/login"
          className="inline-flex items-center text-xs font-medium text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200 transition-colors"
        >
          <ArrowLeft className="w-3.5 h-3.5 mr-1" />
          Back to Sign In
        </Link>
      </div>
    </form>
  );
};
